import React, { type FC } from 'react';
import { FormattedMessage, injectIntl, type WrappedComponentProps } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Textfield from '@atlaskit/textfield';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import Lozenge from '@atlaskit/lozenge';
import SearchIcon from '@atlaskit/icon/core/search';
import AddIcon from '@atlaskit/icon/core/add';

import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import StatusWorkflowInReviewIcon from '@atlaskit/icon-lab/core/status-workflow-in-review';

import type { WorkflowStatus } from '@confluence/approvals/entry-points/ApprovalsContext.ts';

import { ContentStateColor } from '../../ContentStateColor.tsx';
import type { ContentState } from '../types.ts';

import type { DefaultSectionItem } from './ContentStateMenuV2.tsx';
import { i18n } from './contentStateMenuV2I18n.ts';

const styles = cssMap({
	dialog: {
		minWidth: '320px',
		maxWidth: '360px',
	},
	row: {
		alignItems: 'center',
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		justifyContent: 'space-between',
	},
	searchInput: {
		paddingInline: token('space.150'),
		paddingTop: token('space.100'),
	},
	after: {
		paddingInlineEnd: token('space.075'),
	},
	workflowStatusBadge: {
		backgroundColor: token('color.background.neutral.subtle'),
		cursor: 'pointer',
		color: token('color.text.subtlest'),
		gap: token('space.100'),
		alignItems: 'center',
	},
	workflowStatusLabel: {
		alignItems: 'center',
		gap: token('space.100'),
	},
	badgeWrapperStyles: {
		backgroundColor: token('elevation.surface.sunken'),
		padding: token('space.025'),
	},
	iconStyles: {
		marginLeft: token('space.negative.025'),
	},
});

type StatusOptionRowProps = {
	option: ContentState;
	onSelect: (status: ContentState) => void;
	isActive?: boolean;
};

const StatusOptionRow: FC<StatusOptionRowProps> = ({ option, onSelect, isActive }) => {
	const handleClick = () => {
		if (option && option.name) {
			onSelect(option);
		}
	};

	return (
		<ButtonItem
			isSelected={isActive}
			iconBefore={
				<ContentStateColor
					color={option.color}
					restrictionLevel={option.restrictionLevel}
					appearance="contentStateButton"
				/>
			}
			iconAfter={
				isActive ? (
					<span data-testid="status-check-icon">
						<CheckMarkIcon label="" size="medium" color={token('color.icon.success')} />
					</span>
				) : undefined
			}
			onClick={handleClick}
		>
			{option.name}
		</ButtonItem>
	);
};

type WorkflowStatusOptionRowProps = {
	status: WorkflowStatus;
	onSelect: (status: WorkflowStatus) => void;
};

const WorkflowStatusOptionRow: FC<WorkflowStatusOptionRowProps> = ({ status, onSelect }) => {
	const handleClick = () => onSelect(status);

	return (
		<ButtonItem
			iconBefore={
				<Box xcss={styles.iconStyles}>
					<StatusWorkflowInReviewIcon
						label=""
						size="small"
						color={token('color.icon.accent.blue')}
						spacing="none"
					/>
				</Box>
			}
			onClick={handleClick}
			testId={`workflow-status-option-${status.id}`}
		>
			<Inline xcss={styles.row}>
				<Inline xcss={styles.workflowStatusLabel}>
					{status.name}
					{status.allowsReviews && (
						<Lozenge appearance="discovery">
							<FormattedMessage {...i18n.approvalBetaTag} />
						</Lozenge>
					)}
				</Inline>
				<Inline xcss={styles.workflowStatusBadge}>
					<Box xcss={styles.badgeWrapperStyles}>
						<Text size="small" color="color.text.subtlest">
							<FormattedMessage {...i18n.startApproval} />
						</Text>
					</Box>

					<ChevronRightIcon size="small" label="" />
				</Inline>
			</Inline>
		</ButtonItem>
	);
};

type ContentStateMenuV2SelectViewProps = WrappedComponentProps & {
	searchValue: string;
	onSearchChange: (value: string) => void;
	filteredDefaultSection: DefaultSectionItem[];
	filteredCustomOptions: ContentState[];
	hasResults: boolean;
	currentContentState: ContentState | null;
	onSelectStatus: (status: ContentState) => void;
	onSelectWorkflowStatus: (status: WorkflowStatus) => void;
	onAddCustomClick: () => void;
};

const ContentStateMenuV2SelectViewComponent: FC<ContentStateMenuV2SelectViewProps> = ({
	searchValue,
	onSearchChange,
	filteredDefaultSection,
	filteredCustomOptions,
	hasResults,
	currentContentState,
	onSelectStatus,
	onSelectWorkflowStatus,
	onAddCustomClick,
	intl: { formatMessage },
}) => {
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
		onSearchChange(e.currentTarget.value);

	return (
		<Stack xcss={styles.dialog} testId="status-selector-popup-menu">
			<MenuGroup maxHeight={360}>
				<Section>
					<Inline xcss={styles.searchInput}>
						<Textfield
							name="status-search"
							aria-label={formatMessage(i18n.searchPlaceholder)}
							placeholder={formatMessage(i18n.searchPlaceholder)}
							maxLength={20}
							isCompact
							value={searchValue}
							onChange={handleSearchChange}
							elemAfterInput={
								<Box xcss={styles.after}>
									<SearchIcon label="" />
								</Box>
							}
							testId="status-search-input"
						/>
					</Inline>
				</Section>
				<Section isScrollable>
					{filteredDefaultSection.length > 0 && (
						<Section title={formatMessage(i18n.defaultWorkflow)}>
							<MenuGroup>
								{filteredDefaultSection.map((item) =>
									item.type === 'contentState' ? (
										<StatusOptionRow
											key={item.contentState.id}
											option={item.contentState}
											onSelect={onSelectStatus}
											isActive={currentContentState?.id === item.contentState.id}
										/>
									) : (
										<WorkflowStatusOptionRow
											key={`workflow-${item.workflowStatus.id}`}
											status={item.workflowStatus}
											onSelect={onSelectWorkflowStatus}
										/>
									),
								)}
							</MenuGroup>
						</Section>
					)}
					{filteredCustomOptions.length > 0 && (
						<Section title={formatMessage(i18n.customStatuses)}>
							{filteredCustomOptions.map((option) => (
								<StatusOptionRow
									key={option.id}
									option={option}
									onSelect={onSelectStatus}
									isActive={currentContentState?.id === option.id}
								/>
							))}
						</Section>
					)}
					{!hasResults && searchValue.trim() && (
						<Section>
							<ButtonItem
								iconBefore={<AddIcon label="" />}
								description={formatMessage(i18n.addCustomStatusDescription)}
								onClick={onAddCustomClick}
								testId="add-custom-status-button"
							>
								{formatMessage(i18n.addCustomStatus)}
							</ButtonItem>
						</Section>
					)}
				</Section>
			</MenuGroup>
		</Stack>
	);
};

export const ContentStateMenuV2SelectView = injectIntl(ContentStateMenuV2SelectViewComponent);
